import React, { useEffect } from 'react';
import { Navbar } from '../../../components/feature/Navbar';
import { Footer } from '../../../components/feature/Footer';
import { Link } from 'react-router-dom';

export default function MontrealWebDesignTipsPage() {
  useEffect(() => {
    // Update canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', 'https://businesswebsitepro.com/blog/montreal-web-design-tips');

    // Update meta tags
    document.title = 'Web Design Tips for Small Businesses in Montreal, QC | 2024 Guide';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Essential web design tips for Montreal small businesses. Learn how to create a bilingual professional website that attracts Quebec customers and grows your business in 2024.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Montreal web design, Quebec small business websites, bilingual website Montreal, Montreal web designer, business website Montreal, web design tips Montreal');
    }

    // Add geo tags
    let geoPosition = document.querySelector('meta[name="geo.position"]');
    if (!geoPosition) {
      geoPosition = document.createElement('meta');
      geoPosition.setAttribute('name', 'geo.position');
      document.head.appendChild(geoPosition);
    }
    geoPosition.setAttribute('content', '45.5017;-73.5673');

    let geoPlacename = document.querySelector('meta[name="geo.placename"]');
    if (!geoPlacename) {
      geoPlacename = document.createElement('meta');
      geoPlacename.setAttribute('name', 'geo.placename');
      document.head.appendChild(geoPlacename);
    }
    geoPlacename.setAttribute('content', 'Montreal, Quebec, Canada');

    let geoRegion = document.querySelector('meta[name="geo.region"]');
    if (!geoRegion) {
      geoRegion = document.createElement('meta');
      geoRegion.setAttribute('name', 'geo.region');
      document.head.appendChild(geoRegion);
    }
    geoRegion.setAttribute('content', 'CA-QC');

    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Schema.org Article structured data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "Web Design Tips for Small Businesses in Montreal, QC",
          "description": "Essential web design tips for Montreal small businesses to create bilingual professional websites that attract Quebec customers",
          "author": {
            "@type": "Organization",
            "name": "BusinessWebsitePro"
          },
          "publisher": {
            "@type": "Organization",
            "name": "BusinessWebsitePro",
            "logo": {
              "@type": "ImageObject",
              "url": "https://businesswebsitepro.com/logo.png"
            }
          },
          "datePublished": "2024-01-15",
          "dateModified": "2024-01-15",
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": "https://businesswebsitepro.com/blog/montreal-web-design-tips"
          },
          "articleSection": "Web Design",
          "keywords": "Montreal web design, Quebec small business, bilingual website",
          "locationCreated": {
            "@type": "Place",
            "name": "Montreal, Quebec, Canada",
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": "45.5017",
              "longitude": "-73.5673"
            }
          },
          "inLanguage": ["en", "fr"]
        })}
      </script>

      {/* Breadcrumb */}
      <nav className="bg-gray-50 py-4" aria-label="Breadcrumb">
        <div className="max-w-4xl mx-auto px-6">
          <ol className="flex items-center gap-2 text-sm">
            <li><Link to="/" className="text-gray-600 hover:text-[#0d3b66] cursor-pointer">Home</Link></li>
            <li className="text-gray-400">/</li>
            <li><Link to="/blog" className="text-gray-600 hover:text-[#0d3b66] cursor-pointer">Blog</Link></li>
            <li className="text-gray-400">/</li>
            <li className="text-[#0d3b66] font-semibold">Montreal Web Design Tips</li>
          </ol>
        </div>
      </nav>

      {/* Article Header */}
      <article className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <header className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <span className="bg-blue-100 text-blue-700 text-sm font-semibold px-4 py-2 rounded-full">
                Montreal Web Design
              </span>
              <span className="text-gray-500 text-sm">9 min read</span>
            </div>
            
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Web Design Tips for Small Businesses in Montreal, QC
            </h1>
            
            <p className="text-xl text-gray-600 mb-8">
              Essential strategies to create a bilingual professional website that attracts Quebec customers and grows your Montreal business in 2024
            </p>

            <div className="flex items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <i className="ri-calendar-line"></i>
                <time dateTime="2024-01-15">January 15, 2024</time>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-map-pin-line"></i>
                <span>Montreal, Quebec</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-global-line"></i>
                <span>English / Français</span>
              </div>
            </div>
          </header>

          {/* Featured Image */}
          <div className="mb-12 rounded-2xl overflow-hidden">
            <img 
              src="https://public.readdy.ai/ai/img_res/28ed07e12f656d278451798ca4c6e682.jpg"
              alt="Professional web design workspace in Montreal with Old Port view"
              className="w-full h-96 object-cover object-top"
            />
          </div>

          {/* Article Content */}
          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              Montreal is Canada's second-largest city and Quebec's cultural and economic heart. With over 4 million residents in the metropolitan area, a vibrant bilingual culture, and a thriving business scene, Montreal offers unique opportunities for small businesses. Whether you're running a café in the Plateau, a boutique in Old Montreal, or a service business in the West Island, your website is often the first impression potential customers have of your business.
            </p>

            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              In this comprehensive guide, we'll share proven web design tips specifically tailored for Montreal small businesses. These strategies will help you create a bilingual website that not only looks professional but also converts visitors into customers in Quebec's unique market.
            </p>

            {/* Tip 1 - Bilingual */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <span className="w-10 h-10 bg-[#0d3b66] text-white rounded-full flex items-center justify-center text-lg flex-shrink-0">1</span>
                Bilingual Website is Essential
              </h2>
              
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                In Quebec, offering your website in both French and English isn't just good practice—it's often legally required under Bill 96 (Law 14). Montreal's bilingual population expects businesses to communicate in both languages.
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-global-line text-blue-700"></i>
                  Bilingual Website Checklist:
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-blue-700 mt-1 flex-shrink-0"></i>
                    <span><strong>French must be prominent</strong> - Quebec law requires French to be at least as prominent as English</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-blue-700 mt-1 flex-shrink-0"></i>
                    <span><strong>Language switcher</strong> - Easy-to-find toggle between French and English</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-blue-700 mt-1 flex-shrink-0"></i>
                    <span><strong>Professional translation</strong> - Avoid Google Translate; use professional Quebec French translation</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-blue-700 mt-1 flex-shrink-0"></i>
                    <span><strong>All content translated</strong> - Every page, button, form, and error message in both languages</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-blue-700 mt-1 flex-shrink-0"></i>
                    <span><strong>SEO in both languages</strong> - Optimize meta tags, URLs, and content for French and English search</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl border-2 border-blue-500 p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-alert-line text-2xl text-blue-700"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Quebec Law Requirement:</h4>
                    <p className="text-gray-700">
                      Bill 96 (Law 14) requires businesses in Quebec to offer services in French. Your website must have French content that is at least as visible and accessible as English content. Non-compliance can result in fines.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Tip 2 */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <span className="w-10 h-10 bg-[#0d3b66] text-white rounded-full flex items-center justify-center text-lg flex-shrink-0">2</span>
                Optimize for Local Montreal Search
              </h2>
              
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Montreal customers search for local businesses in both French and English. Optimize your website for local search in both languages to maximize visibility.
              </p>

              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Montreal Local SEO Strategy:</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                      <i className="ri-english-input"></i>
                      English SEO:
                    </h4>
                    <ul className="space-y-1 text-sm text-gray-700">
                      <li>• "Montreal web design"</li>
                      <li>• "web designer near me Montreal"</li>
                      <li>• "best [service] in Montreal"</li>
                      <li>• "Montreal Quebec [service]"</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                      <i className="ri-translate"></i>
                      French SEO:
                    </h4>
                    <ul className="space-y-1 text-sm text-gray-700">
                      <li>• "conception web Montréal"</li>
                      <li>• "concepteur web près de moi"</li>
                      <li>• "meilleur [service] à Montréal"</li>
                      <li>• "Montréal Québec [service]"</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded-r-xl p-6">
                <h4 className="font-bold text-gray-900 mb-2">Montreal Neighborhoods to Target:</h4>
                <p className="text-gray-700 text-sm mb-2">Include these areas in your local SEO strategy:</p>
                <div className="grid md:grid-cols-3 gap-2 text-sm text-gray-700">
                  <div>• Old Montreal (Vieux-Montréal)</div>
                  <div>• Plateau Mont-Royal</div>
                  <div>• Downtown (Centre-ville)</div>
                  <div>• Mile End</div>
                  <div>• Westmount</div>
                  <div>• Outremont</div>
                  <div>• Rosemont</div>
                  <div>• Verdun</div>
                  <div>• West Island</div>
                </div>
              </div>
            </section>

            {/* Tip 3 */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <span className="w-10 h-10 bg-[#0d3b66] text-white rounded-full flex items-center justify-center text-lg flex-shrink-0">3</span>
                Mobile-First Design is Critical
              </h2>
              
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Over 75% of Montreal residents use smartphones to search for local businesses. Your bilingual website must look perfect and function flawlessly on mobile devices.
              </p>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="bg-green-50 rounded-xl p-6">
                  <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <i className="ri-check-line text-green-600"></i>
                    Mobile-Friendly Features:
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Responsive design for all screen sizes</li>
                    <li>• Large, easy-to-tap buttons</li>
                    <li>• Fast loading (under 3 seconds)</li>
                    <li>• Click-to-call phone numbers</li>
                    <li>• Easy language switching on mobile</li>
                    <li>• Readable text without zooming</li>
                  </ul>
                </div>

                <div className="bg-red-50 rounded-xl p-6">
                  <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <i className="ri-close-line text-red-600"></i>
                    Avoid These Mobile Mistakes:
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Tiny text requiring zooming</li>
                    <li>• Buttons too close together</li>
                    <li>• Horizontal scrolling</li>
                    <li>• Pop-ups covering content</li>
                    <li>• Slow-loading images</li>
                    <li>• Hidden language switcher</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Tip 4 */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <span className="w-10 h-10 bg-[#0d3b66] text-white rounded-full flex items-center justify-center text-lg flex-shrink-0">4</span>
                Showcase Your Montreal Location
              </h2>
              
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Montreal customers prefer doing business with local companies. Make your Montreal presence obvious and build trust with local elements in both languages.
              </p>

              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-8 mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Ways to Highlight Your Montreal Location:</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <i className="ri-map-pin-line text-2xl text-[#0d3b66] flex-shrink-0"></i>
                    <div>
                      <strong className="text-gray-900">Embed Google Maps</strong>
                      <p className="text-sm text-gray-700">Show your exact Montreal location with an interactive map</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-image-line text-2xl text-[#0d3b66] flex-shrink-0"></i>
                    <div>
                      <strong className="text-gray-900">Montreal Photos</strong>
                      <p className="text-sm text-gray-700">Use images of Old Montreal, Mount Royal, or local landmarks</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-star-line text-2xl text-[#0d3b66] flex-shrink-0"></i>
                    <div>
                      <strong className="text-gray-900">Bilingual Testimonials</strong>
                      <p className="text-sm text-gray-700">Feature reviews from Montreal customers in French and English</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-building-line text-2xl text-[#0d3b66] flex-shrink-0"></i>
                    <div>
                      <strong className="text-gray-900">Service Areas</strong>
                      <p className="text-sm text-gray-700">List Montreal neighborhoods and surrounding areas you serve</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Tip 5 */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <span className="w-10 h-10 bg-[#0d3b66] text-white rounded-full flex items-center justify-center text-lg flex-shrink-0">5</span>
                Clear Bilingual Call-to-Actions
              </h2>
              
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Every page should guide visitors toward taking action in their preferred language. Make it crystal clear what you want Montreal customers to do next.
              </p>

              <div className="grid md:grid-cols-3 gap-6 mb-6">
                <div className="bg-white rounded-xl border-2 border-[#0d3b66] p-6 text-center">
                  <div className="w-16 h-16 bg-[#0d3b66] rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-phone-line text-3xl text-white"></i>
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">Call Now / Appelez</h3>
                  <p className="text-sm text-gray-600">Click-to-call buttons for mobile users</p>
                </div>

                <div className="bg-white rounded-xl border-2 border-[#0d3b66] p-6 text-center">
                  <div className="w-16 h-16 bg-[#0d3b66] rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-calendar-line text-3xl text-white"></i>
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">Book / Réserver</h3>
                  <p className="text-sm text-gray-600">Easy appointment scheduling system</p>
                </div>

                <div className="bg-white rounded-xl border-2 border-[#0d3b66] p-6 text-center">
                  <div className="w-16 h-16 bg-[#0d3b66] rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-mail-line text-3xl text-white"></i>
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">Quote / Soumission</h3>
                  <p className="text-sm text-gray-600">Simple contact forms for inquiries</p>
                </div>
              </div>
            </section>

            {/* Tip 6 */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <span className="w-10 h-10 bg-[#0d3b66] text-white rounded-full flex items-center justify-center text-lg flex-shrink-0">6</span>
                Build Trust with Montreal Customers
              </h2>
              
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Montreal customers value authenticity and cultural sensitivity. Build credibility with trust signals in both languages throughout your website.
              </p>

              <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Trust-Building Elements:</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-shield-check-line text-xl text-white"></i>
                    </div>
                    <div>
                      <strong className="text-gray-900">Security Badges</strong>
                      <p className="text-sm text-gray-700">SSL certificate, secure payment icons, bilingual privacy policy</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-star-smile-line text-xl text-white"></i>
                    </div>
                    <div>
                      <strong className="text-gray-900">Customer Reviews</strong>
                      <p className="text-sm text-gray-700">Google reviews, testimonials in French and English</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-award-line text-xl text-white"></i>
                    </div>
                    <div>
                      <strong className="text-gray-900">Certifications</strong>
                      <p className="text-sm text-gray-700">Industry certifications, awards, professional memberships</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-team-line text-xl text-white"></i>
                    </div>
                    <div>
                      <strong className="text-gray-900">About Us Page</strong>
                      <p className="text-sm text-gray-700">Team photos, company story, Montreal community involvement</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Bonus Montreal Tips */}
            <section className="mb-12">
              <div className="bg-gradient-to-r from-[#0d3b66] to-[#1a5490] rounded-2xl p-8 text-white">
                <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
                  <i className="ri-gift-line text-4xl"></i>
                  Bonus: Montreal-Specific Web Design Tips
                </h2>
                
                <div className="space-y-6">
                  <div className="bg-white/10 rounded-xl p-6">
                    <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
                      <i className="ri-subway-line"></i>
                      Metro Integration
                    </h3>
                    <p className="text-white/90">
                      Many Montreal customers use the Metro. Include STM directions, nearby Metro stations, and parking information on your contact page in both languages.
                    </p>
                  </div>

                  <div className="bg-white/10 rounded-xl p-6">
                    <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
                      <i className="ri-palette-line"></i>
                      Cultural Sensitivity
                    </h3>
                    <p className="text-white/90">
                      Montreal has a rich cultural heritage. Use imagery and messaging that respects both French and English cultures. Avoid stereotypes and ensure equal representation.
                    </p>
                  </div>

                  <div className="bg-white/10 rounded-xl p-6">
                    <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
                      <i className="ri-music-line"></i>
                      Arts and Culture
                    </h3>
                    <p className="text-white/90">
                      Montreal is known for festivals, arts, and culture. Reference local events like Jazz Fest, Just for Laughs, or Osheaga to connect with Montreal customers.
                    </p>
                  </div>

                  <div className="bg-white/10 rounded-xl p-6">
                    <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
                      <i className="ri-restaurant-line"></i>
                    </h3>
                    <p className="text-white/90">
                      Montreal's food scene is world-renowned. If you're in food/hospitality, highlight local ingredients, Quebec specialties, or Montreal culinary traditions.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* FAQ Section */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Frequently Asked Questions</h2>
              
              <div className="space-y-4">
                <details className="bg-gray-50 rounded-xl p-6 cursor-pointer group">
                  <summary className="font-bold text-gray-900 cursor-pointer list-none flex items-center justify-between">
                    <span>Is a bilingual website legally required in Montreal?</span>
                    <i className="ri-arrow-down-s-line text-2xl text-gray-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <p className="text-gray-700 mt-4 leading-relaxed">
                    Yes, under Quebec's Bill 96 (Law 14), businesses must offer services in French. Your website must have French content that is at least as visible and accessible as English content. While you can offer English, French must be prominent. Non-compliance can result in fines from the Office québécois de la langue française (OQLF).
                  </p>
                </details>

                <details className="bg-gray-50 rounded-xl p-6 cursor-pointer group">
                  <summary className="font-bold text-gray-900 cursor-pointer list-none flex items-center justify-between">
                    <span>How much does a bilingual website cost in Montreal?</span>
                    <i className="ri-arrow-down-s-line text-2xl text-gray-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <p className="text-gray-700 mt-4 leading-relaxed">
                    Bilingual websites typically cost 20-40% more than single-language sites due to translation and additional development. Expect to pay $800-$6,000+ depending on complexity. At BusinessWebsitePro, we offer bilingual websites starting at $699 with professional Quebec French translation included.
                  </p>
                </details>

                <details className="bg-gray-50 rounded-xl p-6 cursor-pointer group">
                  <summary className="font-bold text-gray-900 cursor-pointer list-none flex items-center justify-between">
                    <span>Should I use Google Translate for my French content?</span>
                    <i className="ri-arrow-down-s-line text-2xl text-gray-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <p className="text-gray-700 mt-4 leading-relaxed">
                    No! Google Translate often produces awkward or incorrect translations that can damage your credibility with French-speaking customers. Always use professional translation services that understand Quebec French (which differs from European French). Poor translation can also hurt your SEO and legal compliance.
                  </p>
                </details>

                <details className="bg-gray-50 rounded-xl p-6 cursor-pointer group">
                  <summary className="font-bold text-gray-900 cursor-pointer list-none flex items-center justify-between">
                    <span>How long does it take to build a bilingual website?</span>
                    <i className="ri-arrow-down-s-line text-2xl text-gray-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <p className="text-gray-700 mt-4 leading-relaxed">
                    A bilingual website typically takes 2-4 weeks, including time for professional translation. Simple sites can be completed in 2-3 weeks, while complex sites with extensive content may take 4-8 weeks. At BusinessWebsitePro, we streamline the process and often deliver bilingual websites within 2-3 weeks.
                  </p>
                </details>

                <details className="bg-gray-50 rounded-xl p-6 cursor-pointer group">
                  <summary className="font-bold text-gray-900 cursor-pointer list-none flex items-center justify-between">
                    <span>What's the best way to attract Montreal customers through my website?</span>
                    <i className="ri-arrow-down-s-line text-2xl text-gray-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <p className="text-gray-700 mt-4 leading-relaxed">
                    Focus on bilingual local SEO (optimize for both French and English keywords), create a Google Business Profile in both languages, showcase Montreal-specific content and images, feature bilingual testimonials, optimize for mobile, ensure legal compliance with Bill 96, and make it easy for customers to contact you with clear CTAs in both languages.
                  </p>
                </details>
              </div>
            </section>

            {/* Conclusion */}
            <section className="mb-12">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Ready to Build Your Montreal Business Website?</h2>
                <p className="text-lg text-gray-700 mb-6">
                  A professional bilingual website is essential for success in Montreal's unique market. By following these web design tips, you'll create a site that attracts Quebec customers, builds trust, and complies with provincial language laws.
                </p>
                <p className="text-lg text-gray-700 mb-8">
                  At <strong>BusinessWebsitePro</strong>, we specialize in creating professional bilingual websites for Montreal and Quebec small businesses. Our team understands the local market, language requirements, and cultural nuances.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link 
                    to="/pricing" 
                    className="bg-[#0d3b66] text-white px-8 py-4 rounded-xl font-semibold hover:bg-[#0a2d4d] transition-all duration-300 inline-block cursor-pointer whitespace-nowrap"
                  >
                    View Our Pricing
                  </Link>
                  <Link 
                    to="/contact" 
                    className="bg-white text-[#0d3b66] px-8 py-4 rounded-xl font-semibold border-2 border-[#0d3b66] hover:bg-gray-50 transition-all duration-300 inline-block cursor-pointer whitespace-nowrap"
                  >
                    Get a Free Quote
                  </Link>
                </div>
              </div>
            </section>

            {/* Related Articles */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Articles</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <Link to="/blog/toronto-web-design-tips" className="bg-white rounded-xl border-2 border-gray-200 p-6 hover:border-[#0d3b66] transition-all duration-300 cursor-pointer">
                  <h3 className="font-bold text-gray-900 mb-2">Web Design Tips for Toronto Businesses</h3>
                  <p className="text-sm text-gray-600">Essential strategies for Ontario small businesses</p>
                </Link>
                <Link to="/blog/vancouver-web-design-tips" className="bg-white rounded-xl border-2 border-gray-200 p-6 hover:border-[#0d3b66] transition-all duration-300 cursor-pointer">
                  <h3 className="font-bold text-gray-900 mb-2">Web Design Tips for Vancouver Businesses</h3>
                  <p className="text-sm text-gray-600">Essential strategies for BC small businesses</p>
                </Link>
              </div>
            </section>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}